import React, { useEffect, useState } from "react";
import { DELETE, GET, POST } from "../../api/ApiService";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  FirstPage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  LastPage,
} from "@mui/icons-material";
import MainHeader from "../../shared/MainHeader";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import WufooToCase from "../../components/WufooToCase";
import { storeWufooCase } from "../../slices/wufooCaseSlice";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import PreviewIcon from "@mui/icons-material/Preview";
import EncryptionHelper from "../../shared/EncryptionHelper";

const PrivateReferralsPage = () => {
  const [wufooData, setWufooData] = useState();
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [page, setPage] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);

  const [dropdownFilter, setDropdownFilter] = useState("Referrals");
  const [searchText, setSearchText] = useState("");
  const [deleteReferral, setDeleteReferral] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const dispatch = useDispatch();
  const userDetails = useSelector((state) => state.userReducer.userDetails);

  useEffect(() => {
    getWufooData();
  }, [page, rowsPerPage]);

  function TablePaginationActions(props) {
    const { count, page, rowsPerPage, onPageChange } = props;

    return (
      <div style={{ flexShrink: 0, marginLeft: "2.5rem" }}>
        <IconButton onClick={(e) => onPageChange(e, 0)} disabled={page === 0}>
          <FirstPage />
        </IconButton>
        <IconButton
          onClick={(e) => onPageChange(e, page - 1)}
          disabled={page === 0}
        >
          <KeyboardArrowLeft />
        </IconButton>
        <IconButton
          onClick={(e) => onPageChange(e, page + 1)}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        >
          <KeyboardArrowRight />
        </IconButton>
        <IconButton
          onClick={(e) =>
            onPageChange(e, Math.max(0, Math.ceil(count / rowsPerPage) - 1))
          }
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        >
          <LastPage />
        </IconButton>
      </div>
    );
  }

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getWufooData = async () => {
    try {
      setLoading(true);

      const resp = await GET(
        `wufoo/get-data?pageNumber=${
          page + 1
        }&pageSize=${rowsPerPage}&type=Private&filterBy=${dropdownFilter}&searchText=${searchText}`,
      );

      setWufooData(resp?.wufooData);
      setTotalRows(resp?.totalWufooData);
      setLoading(false);
    } catch (err) {
      console.warn(err);
      setLoading(false);
    }
  };

  const convertToCaseHandler = async (data) => {
    const judgeName = data?.JudgeReferral?.trim();
    let createdJudgeContact = null;

    if (judgeName) {
      try {
        const encryptData = EncryptionHelper.encryptData({
          Name: judgeName,
          Type: "Judge",
          Visibility: "public",
          CreatedBy: userDetails?.id,
        });

        const resp = await POST("contact/find-or-create-judge", {
          encryptData,
        });
        createdJudgeContact = resp?.data ?? null;
      } catch (err) {
        console.warn("Judge contact creation failed", err);
        toast.error("Judge contact could not be created automatically.");
      }
    }

    setOpenDialog(true);
    dispatch(
      storeWufooCase({
        ...data,
        createdJudgeContact,
      }),
    );
  };

  const closeDialogHandler = () => {
    getWufooData();
    setOpenDialog(false);
  };

  // dropdownFilter → load data
  useEffect(() => {
    getWufooData();
  }, [dropdownFilter]);

  // debounce for search
  useEffect(() => {
    const delay = setTimeout(() => getWufooData(), 500);
    return () => clearTimeout(delay);
  }, [searchText]);

  const downloadFile = async (filename) => {
    try {
      const response = await GET(
        `get-file-url?filename=${encodeURIComponent(filename)}`,
      );
      const fileUrl = response?.url;
      if (!fileUrl) return;

      const res = await fetch(fileUrl);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.click();

      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error(err);
    }
  };

  const previewBtnHandler = async (filename) => {
    try {
      const response = await GET(
        `get-file-url?filename=${encodeURIComponent(filename)}`,
      );
      if (response?.url) {
        window.open(response.url, "_blank");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const openDeleteReferralDialog = (item) => {
    setDeleteReferral(item);
  };

  const closeDeleteReferralDialog = () => {
    if (deleteLoading) {
      return;
    }

    setDeleteReferral(null);
  };

  const deleteReferralHandler = async () => {
    if (!deleteReferral) {
      return;
    }

    try {
      setDeleteLoading(true);
      await DELETE(`wufoo/delete/${deleteReferral.WufooID}`);
      toast.success("Referral deleted successfully.");
      setDeleteReferral(null);
      getWufooData();
    } catch (err) {
      console.warn(err);
      toast.error("Unable to delete referral.");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div>
      <MainHeader headingText={"Private Referrals"} />

      {loading ? (
        <Stack justifyContent="center" alignItems="center" height={400}>
          <CircularProgress />
        </Stack>
      ) : (
        <>
          <Stack gap={2} direction="row" alignItems="center" sx={{ mb: 2 }}>
            {/* 🔍 Search Box */}
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search referrals..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                width: 220,
                "& .MuiInputBase-input": {
                  padding: "6px 10px",
                  fontSize: "13px",
                },
              }}
            />

            <Select
              size="small"
              value={dropdownFilter}
              onChange={(e) => setDropdownFilter(e.target.value)}
              sx={{
                width: 180,
                "& .MuiSelect-select": {
                  padding: "6px 10px",
                  fontSize: "13px",
                },
              }}
            >
              <MenuItem value={"Referrals"}>Referrals</MenuItem>
              <MenuItem value={"Converted to Case"}>Converted to Case</MenuItem>
              <MenuItem value={"All"}>All</MenuItem>
            </Select>
          </Stack>

          {wufooData?.length > 0 ? (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Actions</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Created At</TableCell>
                      <TableCell>Referral Date</TableCell>
                      <TableCell>Case Name</TableCell>
                      <TableCell>Court</TableCell>
                      <TableCell>Judge</TableCell>
                      <TableCell>File Number</TableCell>
                      <TableCell>Adjourn Date</TableCell>
                      <TableCell>Payment Responsibility</TableCell>

                      <TableCell>Visiting Party Name</TableCell>
                      <TableCell>VP Payment</TableCell>
                      <TableCell>VP Email</TableCell>
                      <TableCell>VP Phone</TableCell>
                      <TableCell>VP Attorney Name</TableCell>
                      <TableCell>VP Attorney Email</TableCell>
                      <TableCell>VP Attorney Phone</TableCell>

                      <TableCell>CP Name</TableCell>
                      <TableCell>CP Payment</TableCell>
                      <TableCell>CP Email</TableCell>
                      <TableCell>CP Phone</TableCell>
                      <TableCell>CP Attorney Name</TableCell>
                      <TableCell>CP Attorney Email</TableCell>
                      <TableCell>CP Attorney Phone</TableCell>

                      <TableCell>Order of Protection</TableCell>
                      <TableCell>OOP Doc</TableCell>
                      <TableCell>Appointment Doc</TableCell>
                      <TableCell>Petition Doc</TableCell>
                      <TableCell>Additional Doc</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {wufooData?.map((item) => (
                      <TableRow key={item.WufooID}>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="outlined"
                              onClick={() => convertToCaseHandler(item)}
                            >
                              Convert to case
                            </Button>
                            <IconButton
                              color="error"
                              aria-label="delete referral"
                              onClick={() => openDeleteReferralDialog(item)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        </TableCell>

                        <TableCell>{item.Name}</TableCell>
                        <TableCell>{item.PhoneNumber}</TableCell>
                        <TableCell>{item.Email}</TableCell>
                        <TableCell>
                          {item.CreatedAt
                            ? moment(item.CreatedAt).format("LLL")
                            : "null"}
                        </TableCell>
                        <TableCell>
                          {item.ReferralDate
                            ? moment(item.ReferralDate).format("LL")
                            : "null"}
                        </TableCell>

                        <TableCell>{item.CaseName}</TableCell>
                        <TableCell>{item.Court}</TableCell>
                        <TableCell>{item.JudgeReferral}</TableCell>
                        <TableCell>{item.FileNumber}</TableCell>

                        <TableCell>
                          {item.AdjournedDate
                            ? moment(item.AdjournedDate).format("LL")
                            : "null"}
                        </TableCell>

                        <TableCell>{item.PaymentResponsibility}</TableCell>

                        <TableCell>{item.VisitingPartyName}</TableCell>
                        <TableCell>{item.VPPaymentResponsibility}</TableCell>
                        <TableCell>{item.VPEmail}</TableCell>
                        <TableCell>{item.VPPhone}</TableCell>
                        <TableCell>{item.VPAttoneyName}</TableCell>
                        <TableCell>{item.VPAttoneyEmail}</TableCell>
                        <TableCell>{item.VPAttoneyPhone}</TableCell>

                        <TableCell>{item.CPName}</TableCell>
                        <TableCell>{item.CPPaymentResponsibility}</TableCell>
                        <TableCell>{item.CPEmail}</TableCell>
                        <TableCell>{item.CPPhone}</TableCell>
                        <TableCell>{item.CPAttoneyName}</TableCell>
                        <TableCell>{item.CPAttoneyEmail}</TableCell>
                        <TableCell>{item.CPAttoneyPhone}</TableCell>

                        <TableCell>{item.OrderOfProtection}</TableCell>

                        {/* Docs Preview + Download */}
                        <TableCell>
                          {item.OOPDoc ? (
                            <Stack direction="row">
                              <PreviewIcon
                                sx={{ cursor: "pointer" }}
                                onClick={() => previewBtnHandler(item.OOPDoc)}
                              />
                              <DownloadIcon
                                sx={{ cursor: "pointer" }}
                                onClick={() => downloadFile(item.OOPDoc)}
                              />
                            </Stack>
                          ) : (
                            "null"
                          )}
                        </TableCell>

                        <TableCell>
                          {item.AppointmentDoc ? (
                            <Stack direction="row">
                              <PreviewIcon
                                sx={{ cursor: "pointer" }}
                                onClick={() =>
                                  previewBtnHandler(item.AppointmentDoc)
                                }
                              />
                              <DownloadIcon
                                sx={{ cursor: "pointer" }}
                                onClick={() =>
                                  downloadFile(item.AppointmentDoc)
                                }
                              />
                            </Stack>
                          ) : (
                            "null"
                          )}
                        </TableCell>

                        <TableCell>
                          {item.petitionDoc ? (
                            <Stack direction="row">
                              <PreviewIcon
                                sx={{ cursor: "pointer" }}
                                onClick={() =>
                                  previewBtnHandler(item.petitionDoc)
                                }
                              />
                              <DownloadIcon
                                sx={{ cursor: "pointer" }}
                                onClick={() => downloadFile(item.petitionDoc)}
                              />
                            </Stack>
                          ) : (
                            "null"
                          )}
                        </TableCell>

                        <TableCell>
                          {item.additionalDoc ? (
                            <Stack direction="row">
                              <PreviewIcon
                                sx={{ cursor: "pointer" }}
                                onClick={() =>
                                  previewBtnHandler(item.additionalDoc)
                                }
                              />
                              <DownloadIcon
                                sx={{ cursor: "pointer" }}
                                onClick={() => downloadFile(item.additionalDoc)}
                              />
                            </Stack>
                          ) : (
                            "null"
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[100, 250, 300]}
                component="div"
                count={totalRows}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </>
          ) : (
            <Stack justifyContent="center" alignItems="center" height={200}>
              No Data Found
            </Stack>
          )}
        </>
      )}

      {/* Convert to Case Dialog */}
      <Dialog open={openDialog} fullScreen onClose={closeDialogHandler}>
        <WufooToCase closeDialogHandler={closeDialogHandler} />
      </Dialog>

      <Dialog
        open={Boolean(deleteReferral)}
        onClose={closeDeleteReferralDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Referral</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this referral?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeleteReferralDialog} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={deleteReferralHandler}
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PrivateReferralsPage;
